import { Arrow } from "../abstract/AbstractArrow";
import { CANVAS_ARROW_COLOR, CANVAS_ARROW_HIGHLIGHT_COLOR, CANVAS_BACKGROUND_COLOR, CANVAS_ITEM_GLOW_COLOR } from "../styles/Canvas";
import { Line } from "../clases/Line";
import { Values } from "../clases/Values";
import { IField } from "../interfaces/Field";
import { DBItem } from "../interfaces/Record";
import { AbstractShape } from "../abstract/Shape";
import { conditionShow } from "../utils/ContainerUtils";
import { RelationDirection } from "../enums/RelationDirection";
import { convertIntoAnchorEnum, convertIntoArrowEnum, convertIntoArrowTypeEnum, convertIntoRelationDirectionEnum } from "../utils/utils";
import { IConnections } from "../interfaces/Connections";
import { ArrowStyle, ArrowType } from "../enums/ArrowType";
import { Anchor } from "../enums/Anchor";

export class ConnectionRenderer extends Arrow{
    from: string;
    to: string;
    lineColor: string = CANVAS_ARROW_COLOR;
    highlightColor: string = CANVAS_ARROW_HIGHLIGHT_COLOR;
    currentLineColor: string = "";
    id: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    iTo?: AbstractShape | undefined;
    iFrom: AbstractShape;
    show: boolean = true;
    type: string | undefined;
    name: string | undefined;
    relation!: DBItem;
    dbPropierties!: any;
    fields: IField[] = [];
    arrowType!: ArrowType;
    arrowStyle: ArrowStyle;
    isHovered: boolean = false;
    isFocus: boolean = false;
    boxWidth: number = 0;
    boxHeight: number = 0;
    boxX: number = 0;
    boxY: number = 0;
    fontSize: number = 12;
    fontFamily: string = "Teko";
    bounding: any;
    textPos!: Values;
    first: boolean = false;
    direction!: RelationDirection;

    anchorFrom: Anchor = Anchor.ANY;
    anchorTo: Anchor = Anchor.ANY;
    textPositionPercent: number = 50;
    lArrowHorizontalSpacing: number = 0;
    lArrowVerticalSpacing: number = 0;

    isInView: boolean = false;

    scale: number = 1;

    constructor(
        id: string,
        from: string,
        to: string,
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        iFrom: AbstractShape,
        iTo: AbstractShape | undefined,
        type: string,
        name: string,
        lineColor: string = CANVAS_ARROW_COLOR,
        arrowStyle?: ArrowStyle,
        arrowType?: ArrowType,
    ) {
        super();
        this.id = id;
        this.from = from;
        this.to = to;
        this.x1 = x1;
        this.y1 = y1;
        this.x2 = x2;
        this.y2 = y2;
        this.lineColor = lineColor;
        this.iFrom = iFrom;
        this.iTo = iTo;
        this.type = type;
        this.name = name;
        this.currentLineColor = lineColor;
        this.arrowStyle = arrowStyle || ArrowStyle.SOLID;
        this.arrowType = arrowType || ArrowType.SQUARED;
    }

    calculateScale(ctx: CanvasRenderingContext2D){
        if(this.iTo){
            let fromSize = this.iFrom.getSize();
            let toSize = this.iTo.getSize();
            this.scale = (Math.min(Math.min(fromSize.x, fromSize.y), Math.min(toSize.x, toSize.y))/100) * 1.6;
            this.instanciateText(ctx);
            return;
        }
        let fromSize = this.iFrom.getSize();
        this.scale = (Math.min(fromSize.x, fromSize.y)/100) * 1.6;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.direction === RelationDirection.NO_DRAW) return;
        let show = conditionShow(this, this.iFrom, this.iTo!);
        if (!show) return;

        this.calculateScale(ctx);

        let arrow = this.drawStraightArrow.bind(this);
        switch (this.arrowType) {
            case ArrowType.SQUARED:
                arrow = this.drawSquaredArrow.bind(this);
                break;
            case ArrowType.L_SHAPED:
                arrow = this.drawLArrow.bind(this);
                break;
            default:
                arrow = this.drawStraightArrow.bind(this);
                break;
        }
        let lineDashed = this.scale*3;
        let lineDotted = this.scale*2;
        switch (this.arrowStyle) {
            case ArrowStyle.SOLID:
                arrow(ctx);
                break;
            case ArrowStyle.DASHED:
                arrow(ctx, new Values(lineDashed, lineDashed));
                break;
            case ArrowStyle.DOTTED:
                arrow(ctx, new Values(lineDotted/2.5, lineDotted));
                break;
            default:
                arrow(ctx);
                break;
        }
    }

    drawSquaredArrow(ctx: CanvasRenderingContext2D, lineType: Values = new Values(0, 0)) {
        const points = this.calculateSquareArrow(this);
        this.drawOrthogonalArrow(ctx, points, lineType);
    }

    drawLArrow(ctx: CanvasRenderingContext2D, lineType: Values = new Values(0, 0)) {
        this.drawOrthogonalArrow(ctx, this.calculateLArrow(this), lineType);
    }

    private drawOrthogonalArrow(
        ctx: CanvasRenderingContext2D,
        points: Values[],
        lineType: Values = new Values(0, 0),
    ) {
        let shortestSegment = Number.POSITIVE_INFINITY;
        for (let index = 0; index < points.length - 1; index++) {
            const distance = this.distanceBetween2Points(points[index], points[index + 1]);
            if (distance === 0) continue;
            shortestSegment = Math.min(shortestSegment, distance);
        }
        let radius = (!Number.isFinite(shortestSegment) || shortestSegment <= this.scale*5) ? 0 : this.scale*3;

        this.drawPoints(ctx, points, radius, lineType);

        if (this.name) {
            this.textPos = this.calculateTextWithPercentage(this.bounding, points, this.textPositionPercent);
            this.drawTextBox(ctx, this.fontSize, this.bounding, this.textPos);
            this.drawText(ctx, this.fontSize, this.fontFamily, this.textPos);
        }

        this.chooseArrowHead(ctx);
    }

    drawStraightArrow(ctx: CanvasRenderingContext2D, lineType: Values = new Values(0, 0)){
        this.strc = this.getCords(this, this.iFrom, true);
        this.endc = this.getCords(this, this.iTo!, false);

        let points = [this.strc, this.endc];
        this.drawPoints(ctx, points, 0, lineType);

        if (this.name) {
            this.textPos = this.calculateTextWithPercentage(this.bounding, points, this.textPositionPercent);
            this.drawTextBox(ctx, this.fontSize, this.bounding, this.textPos);
            this.drawText(ctx, this.fontSize, this.fontFamily, this.textPos);
        }

        this.chooseStrightArrowHead(ctx, points[0], points[1]);
    }

    drawStraightArrowNoTo(ctx: CanvasRenderingContext2D, from: AbstractShape, lineType: Values = new Values(0, 0)){
        let points = [this.getCords(this, from, true), new Values(this.x2, this.y2)];
        this.drawPoints(ctx, points, 0, lineType);
        this.drawArrowhead(ctx, points[0].x, points[0].y, points[1].x, points[1].y);
    }

    drawPoints(ctx: CanvasRenderingContext2D, points: Values[], radius: number, lineType: Values = new Values(0, 0)) {
        ctx.save();
        // ctx.shadowBlur = 15;
        // ctx.shadowColor = CANVAS_ITEM_GLOW_COLOR;
        ctx.strokeStyle = this.currentLineColor;
        // ctx.lineWidth = this.scale ? this.scale : 1.4; //1.4
        ctx.lineWidth = this.scale; //1.4
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        ctx.setLineDash([lineType.x, lineType.y]);
        for (let index = 1; index < points.length; index++) {
            let point1 = points[index];
            let point2 = points[index + 1];
            if (!point2) {
                ctx.lineTo(point1.x, point1.y);
                break;
            }
            ctx.arcTo(point1.x, point1.y, point2.x, point2.y, radius);
        }
        ctx.stroke();
        ctx.restore();
    }

    chooseStrightArrowHead(ctx: CanvasRenderingContext2D, p1: Values, p2: Values) {
        switch (this.direction) {
            case RelationDirection.CHILD_TO_FATHER:
                this.drawArrowhead(ctx, p1.x, p1.y, p2.x, p2.y);
                break;
            case RelationDirection.FATHER_TO_CHILD:
                this.drawArrowhead(ctx, p2.x, p2.y, p1.x, p1.y);
                break;
            case RelationDirection.BIDIRECTIONAL:
                this.drawArrowhead(ctx, p1.x, p1.y, p2.x, p2.y);
                this.drawArrowhead(ctx, p2.x, p2.y, p1.x, p1.y);
                break;
            default:
                this.drawArrowhead(ctx, p1.x, p1.y, p2.x, p2.y);
        }
    }

    chooseArrowHead(ctx: CanvasRenderingContext2D) {
        if (this.squarePath.length < 2) return;

        const start = this.squarePath[0];
        const second = this.squarePath[1];
        const beforeEnd = this.squarePath[this.squarePath.length - 2];
        const end = this.squarePath[this.squarePath.length - 1];

        switch (this.direction) {
            case RelationDirection.CHILD_TO_FATHER:
                this.drawArrowhead(ctx, beforeEnd.x, beforeEnd.y, end.x, end.y);
                break;
            case RelationDirection.FATHER_TO_CHILD:
                this.drawArrowhead(ctx, second.x, second.y, start.x, start.y);
                break;
            case RelationDirection.BIDIRECTIONAL:
                this.drawArrowhead(ctx, beforeEnd.x, beforeEnd.y, end.x, end.y);
                this.drawArrowhead(ctx, second.x, second.y, start.x, start.y);
                break;
            default:
                this.drawArrowhead(ctx, beforeEnd.x, beforeEnd.y, end.x, end.y);
        }
    }

    instanciateText(ctx: CanvasRenderingContext2D) {
        if (this.name) {
            this.fontSize = this.scale*10;
            const fontSize = this.fontSize;
            const fontFamily = "Teko";
            this.bounding = this.getTextWidth(this.name, fontSize, fontFamily, ctx, this.scale*17.5);
            this.first = true;
        }
    }

    private drawTextBox(ctx: CanvasRenderingContext2D, fontSize: number, bounding: any, textPos: Values) {
        if (!this.name) return;
        const padding = this.scale*3;
        this.boxX = textPos.x - padding;
        this.boxY = textPos.y - fontSize - (padding - (this.scale));
        this.boxWidth = bounding.width + padding * 2;
        this.boxHeight = fontSize + padding * 2;
        ctx.fillStyle = CANVAS_BACKGROUND_COLOR;
        ctx.fillRect(this.boxX, this.boxY, this.boxWidth, this.boxHeight);
    }

    private drawText(ctx: CanvasRenderingContext2D, fontSize: number, fontFamily: string, textPos: Values) {
        if (!this.name) return;
        ctx.font = `${fontSize}px ${fontFamily}`;
        ctx.fillStyle = (this.isHovered || this.isFocus) ? this.highlightColor : CANVAS_ARROW_COLOR;
        ctx.fillText(this.name, textPos.x, textPos.y);
    }

    private drawArrowhead(
        ctx: CanvasRenderingContext2D,
        x1: number,
        y1: number,
        x2: number,
        y2: number
    ) {
        const headLength = this.scale*5; // Arrowhead size
        const angle = Math.atan2(y2 - y1, x2 - x1);

        // Coordinates for the two points of the arrowhead
        const arrowX1 = x2 - headLength * Math.cos(angle - Math.PI / 6);
        const arrowY1 = y2 - headLength * Math.sin(angle - Math.PI / 6);

        const arrowX2 = x2 - headLength * Math.cos(angle + Math.PI / 6);
        const arrowY2 = y2 - headLength * Math.sin(angle + Math.PI / 6);

        // Draw the arrowhead
        ctx.beginPath();
        ctx.moveTo(x2, y2);
        ctx.lineTo(arrowX1, arrowY1);
        ctx.lineTo(arrowX2, arrowY2);
        ctx.lineTo(x2, y2);
        ctx.fillStyle = this.currentLineColor;
        ctx.fill();
    }

    isMouseOverConnection(mouseX: number, mouseY: number): boolean {
        let mouse = new Values(mouseX, mouseY);
        if(this.arrowType !== ArrowType.DIRECT){
            return this.isMouseOverSquare(mouse);
        }else{
            return this.isMouseOverStraight(mouse);
        }
        
    }

    isMouseOverStraight(mouse: Values): boolean {
        let line1 = new Line(this.strc, this.endc);
        let lines: Line[] = [line1];
        return this.calculateDistanceBetweenLines(mouse, lines);
    }

    isMouseOverSquare(mouse: Values): boolean {
        let lines: Line[] = [];
        for (let index = 0; index < this.squarePath.length - 1; index++) {
            const start = this.squarePath[index];
            const end = this.squarePath[index + 1];
            if (start.x === end.x && start.y === end.y) continue;
            lines.push(new Line(start, end));
        }
        return this.calculateDistanceBetweenLines(mouse, lines);
    }

    calculateDistanceBetweenLines(mouse: Values, lines: Line[]): boolean {
        // Calculate the distance from the mouse to the line segment
        for (const line of lines) {
            let res = this.isPointNearLine(mouse, line, this.scale);
            if (res) return res;
        }

        if (this.boxContainsPoint(mouse.x, mouse.y)) {
            return true;
        }

        return false;
    }

    boxContainsPoint(px: number, py: number): boolean {
        return px >= this.boxX && px <= this.boxX + this.boxWidth && py >= this.boxY && py <= this.boxY + this.boxHeight;
    }

    setHover(hover: boolean) {
        this.isHovered = hover;
        this.determineHightlight();
    }

    determineHightlight() {
        if (this.isHovered || this.isFocus) {
            this.currentLineColor = this.highlightColor;
        } else {
            this.currentLineColor = this.lineColor;
        }
    }

    getHover() {
        return this.isHovered;
    }

    setFocus(focus: boolean = true) {
        this.isFocus = focus;
        this.determineHightlight();
    }

    getFocus() {
        return this.isFocus;
    }

    changeType(arrow: ArrowStyle) {
        this.arrowStyle = arrow;
    }

    getIsInView(){
        return this.isInView;
    }

    setIsInView(value: boolean){
        this.isInView = value;
    }

    getLabels() {
        return this.relation.labels[0];
    }

    setRelation(rel: DBItem | undefined) {
        if (!rel) { this.name = "Relation not Founded"; return; }
        this.name = rel.properties.title;
        this.relation = rel;
        this.setDBPropierties(this.relation.properties);
        this.first = false;
    }

    setConnection(conn: IConnections | undefined) {
        if (!conn) return;
        this.arrowStyle = convertIntoArrowEnum(conn.arrowStyle);
        this.arrowType = convertIntoArrowTypeEnum(conn.arrowType);
        this.anchorFrom = convertIntoAnchorEnum(conn.anchorFrom);
        this.anchorTo = convertIntoAnchorEnum(conn.anchorTo);
        this.textPositionPercent = this.normalizeTextPositionPercent(conn.textPositionPercent);
        this.lArrowHorizontalSpacing = Number.isFinite(Number(conn.lArrowHorizontalSpacing)) ? Number(conn.lArrowHorizontalSpacing) : 0;
        this.lArrowVerticalSpacing = Number.isFinite(Number(conn.lArrowVerticalSpacing)) ? Number(conn.lArrowVerticalSpacing) : 0;
    }

    setDBPropierties(propierties: any) {
        this.dbPropierties = propierties;
        this.direction = convertIntoRelationDirectionEnum(this.dbPropierties.directionUI);
        this.fields = [];
        for (const key in this.dbPropierties) {
            this.fields = [...this.fields, { name: key, value: this.dbPropierties[key] }];
        }
        this.fields = this.fields.reverse();
    }

    transformToIConnection(): IConnections {
        return {
            from: this.from,
            to: this.to,
            lineColor: this.lineColor,
            type: this.type,
            relation: (this.relation) ? this.relation.properties.__EDT_UUID__ : undefined,
            arrowStyle: this.arrowStyle,
            arrowType: this.arrowType,
            anchorFrom: this.anchorFrom,
            anchorTo: this.anchorTo,
            textPositionPercent: this.textPositionPercent,
            lArrowHorizontalSpacing: this.lArrowHorizontalSpacing,
            lArrowVerticalSpacing: this.lArrowVerticalSpacing,
        } as IConnections;
    }

    private normalizeTextPositionPercent(value: unknown): number {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return 50;
        return Math.max(0, Math.min(100, parsed));
    }
}
